import React from 'react';
import authPageStyle from './authPage.module.scss';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { withAnonymousUserLayout } from 'acscp/components/layout/anonymousUserLayout';
import { CreateAccountForm } from 'acscp/components/auth/createAccountForm';
import { JetPortalWelcome } from 'acscp/components/auth/jetPortalWelcome';
import { JetPortalFeatures } from 'acscp/components/auth/features/jetPortalFeatures';
import { ForgotPasswordLinkPlacement, LoginForm } from '../auth/loginForm';
import classNames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';
import formStyle from 'acscp/components/common/form.module.scss';
import {
    AuthPageProps,
    mapStateToAuthPageProps
} from 'acscp/components/pages/authPage';
import { LoginPageVariant } from 'acscp/store/loginPageVariant';
import { home } from 'acscp/components/routeHelper';

const messages = defineMessages({
    welcome: {
        id: 'web.components.pages.loginOrCreateAccount.welcome',
        defaultMessage: 'Welcome to the ACS JetPortal. ' +
            'Where you can gain access to everything you need for your private jet charter, whenever and wherever you need it.'
    },
    loginDescription: {
        id: 'web.components.pages.loginOrCreateAccount.loginDescription',
        defaultMessage: 'If you have an account, please log in to access all of your benefits:'
    }
});

type LoginOrCreateAccountPageProps = AuthPageProps & {
    showLoginForm: boolean;
    showCreateAccountForm: boolean;
};

export class LoginOrCreateAccountPagePresentational
    extends React.Component<LoginOrCreateAccountPageProps> {

    private getSearchParameters(): URLSearchParams {
        return new URLSearchParams(this.props.location.search);
    }

    private getRedirectTo(): string {
        return this.getSearchParameters().get('redirect') || home();
    }

    private redirectIfLoggedIn(): void {
        if (this.props.isLoggedIn || (this.doesTargetAllowGuestAccess() && this.props.isGuestLogin)) {
            this.props.history.push(this.getRedirectTo());
        }
    }

    private doesTargetAllowGuestAccess(): boolean {
        return !this.getSearchParameters().get('fullLoginRequired');
    }

    componentDidMount(): void {
        this.redirectIfLoggedIn();
    }

    componentDidUpdate(): void {
        this.redirectIfLoggedIn();
    }

    render(): JSX.Element {
        const useMobileLayout = USE_MOBILE_APP_LOGIN_PAGE;

        const showContinueAsGuest = this.props.stateAllowsGuestAccess && this.doesTargetAllowGuestAccess();

        return (
            <div
                className={classNames(
                    authPageStyle.LoginOrCreateAccountScreen,
                    authPageStyle.pageContainer,
                    {
                        [authPageStyle.pageContainerResponsive]: !useMobileLayout,
                        [authPageStyle.alternativePageLayout]: ENABLE_CREATE_ACCOUNT && showContinueAsGuest
                    })}
            >
                <div className={authPageStyle.welcomeWrapper}>
                    <JetPortalWelcome welcomeMessage={!useMobileLayout ? messages.welcome : undefined} />
                </div>
                <div className={classNames(authPageStyle.formsContainer, {[authPageStyle.formsContainerMobile]: useMobileLayout})}>
                    {this.props.showLoginForm && (
                        <LoginForm
                            location={this.props.location}
                            loginPageVariant={LoginPageVariant.JetPortal}
                            showLoginHeader={!useMobileLayout}
                            introText={!useMobileLayout ? messages.loginDescription : undefined}
                            forgotPasswordLinkPlacement={
                                useMobileLayout ? ForgotPasswordLinkPlacement.Top : ForgotPasswordLinkPlacement.Bottom}
                            showContinueAsGuest={showContinueAsGuest}
                            underFormContent={ENABLE_CREATE_ACCOUNT && USE_MOBILE_APP_LOGIN_PAGE && (
                                <Link to={'/create-account'} className={formStyle.FormButton}>
                                    <FormattedMessage
                                        id={'web.components.pages.loginOrCreateAccount.createAccountButton'}
                                        defaultMessage={'CREATE AN ACCOUNT'}
                                    />
                                </Link>
                            )}
                        />
                    )}
                    {ENABLE_CREATE_ACCOUNT && this.props.showCreateAccountForm && (
                        <CreateAccountForm redirectToAfterSignUp={this.getRedirectTo()} />
                    )}
                </div>
                {!useMobileLayout && <div className={authPageStyle.featuresWrapper}>
                    <JetPortalFeatures fourColumnLayout={ENABLE_CREATE_ACCOUNT && showContinueAsGuest} />
                </div>}
            </div>
        );
    }
}

const LoginOrCreateAccountComponent = withAnonymousUserLayout(
    connect(mapStateToAuthPageProps)(
        LoginOrCreateAccountPagePresentational
    )
);

const CreateAccount: React.FC<RouteComponentProps<any>> = props => (
    <LoginOrCreateAccountComponent
        showLoginForm={false}
        showCreateAccountForm={true}
        {...props}
    />
);

const Login: React.FC<RouteComponentProps<any>> = props => (
    <LoginOrCreateAccountComponent
        showLoginForm={true}
        showCreateAccountForm={false}
        {...props}
    />
);

const LoginOrCreateAccount: React.FC<RouteComponentProps<any>> = props => (
    <LoginOrCreateAccountComponent
        showLoginForm={true}
        showCreateAccountForm={true}
        {...props}
    />
);

export { CreateAccount, Login, LoginOrCreateAccount };
